<template>
  <div class="cover-enlargeable">
    <button
      ref="trigger"
      type="button"
      class="cover-enlargeable__trigger"
      :aria-label="$t('bookInfo.enlargeCover', { title: title })"
      @click="open"
      @mousemove="onHoverMove"
      @mouseleave="onHoverEnd"
    >
      <book-cover-image
        :src="src"
        :alt="$t('bookInfo.bookCoverAlt', { title: title })"
      />
      <v-fade-transition>
        <span
          v-show="hoverActive"
          class="cover-enlargeable__lens"
          :style="lensStyle"
          aria-hidden="true"
        />
      </v-fade-transition>
    </button>

    <v-fade-transition>
      <div
        v-show="hoverActive && src"
        class="cover-enlargeable__zoom-panel"
        :style="panelStyle"
        aria-hidden="true"
      />
    </v-fade-transition>

    <ekitabu-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="cover-viewer-transition"
      content-class="cover-enlargeable__dialog"
    >
      <div
        class="cover-viewer"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('bookInfo.enlargedCoverAlt', { title: title })"
        @keydown.esc.stop="close"
        @click.self="close"
      >
        <ekitabu-button
          icon
          class="cover-viewer__close"
          :aria-label="$t('common.close')"
          @click="close"
        >
          <ekitabu-icon :size="closeIconSize">mdi-close</ekitabu-icon>
        </ekitabu-button>

        <book-cover-image
          :src="src"
          :alt="$t('bookInfo.enlargedCoverAlt', { title: title })"
          class="cover-viewer__image"
          contain
          draggable="false"
          @click.native="close"
        />
      </div>
    </ekitabu-dialog>
  </div>
</template>

<script>
import BookCoverImage from "@/components/BookCoverImage";
import EkitabuDialog from "@/components/EkitabuDialog";

const HOVER_ZOOM_FACTOR = 2.5;
const HOVER_PANEL_WIDTH_PX = 380;
const MIN_HOVER_TRIGGER_SIZE_PX = 80;
const CLOSE_ICON_SIZE_PX = 22;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default {
  name: "BookCoverEnlargeable",
  components: {
    BookCoverImage,
    EkitabuDialog,
  },
  props: {
    src: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      dialog: false,
      hoverActive: false,
      lensStyle: {},
      panelStyle: {},
      closeIconSize: CLOSE_ICON_SIZE_PX,
    };
  },
  methods: {
    open() {
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    onHoverMove(event) {
      if (!this.src || event.pointerType === "touch") return;
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      if (
        rect.width < MIN_HOVER_TRIGGER_SIZE_PX ||
        rect.height < MIN_HOVER_TRIGGER_SIZE_PX
      ) {
        return;
      }

      // Lens is sized so the magnified view exactly fills the side panel —
      // panelHeight tracks the trigger's aspect ratio, lens = panel / zoom.
      const panelWidth = HOVER_PANEL_WIDTH_PX;
      const panelHeight = panelWidth * (rect.height / rect.width);
      const lensWidth = panelWidth / HOVER_ZOOM_FACTOR;
      const lensHeight = panelHeight / HOVER_ZOOM_FACTOR;

      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      const lensX = clamp(cursorX - lensWidth / 2, 0, rect.width - lensWidth);
      const lensY = clamp(cursorY - lensHeight / 2, 0, rect.height - lensHeight);

      this.hoverActive = true;
      this.lensStyle = {
        width: `${lensWidth}px`,
        height: `${lensHeight}px`,
        transform: `translate3d(${lensX}px, ${lensY}px, 0)`,
      };
      this.panelStyle = {
        width: `${panelWidth}px`,
        height: `${panelHeight}px`,
        backgroundImage: `url("${this.src}")`,
        backgroundSize: `${rect.width * HOVER_ZOOM_FACTOR}px ${rect.height * HOVER_ZOOM_FACTOR}px`,
        backgroundPosition: `${-lensX * HOVER_ZOOM_FACTOR}px ${-lensY * HOVER_ZOOM_FACTOR}px`,
      };
    },
    onHoverEnd() {
      this.hoverActive = false;
    },
  },
};
</script>

<!--
  Custom transition classes for the click-to-enlarge dialog. Vuetify's v-dialog
  teleports its wrapper out of the component subtree, so scoped styles cannot
  reach it — these rules must live in a global block.
-->
<style lang="scss">
@import "@/styles/variables.scss";

$cover-viewer-enter-duration: 120ms;
$cover-viewer-leave-duration: 240ms;

.cover-viewer-transition-enter-active {
  transition: opacity $cover-viewer-enter-duration linear;
}
.cover-viewer-transition-leave-active {
  transition: opacity $cover-viewer-leave-duration $book-ease-out;
}
.cover-viewer-transition-enter,
.cover-viewer-transition-leave-to {
  opacity: 0;
}
</style>

<style scoped lang="scss">
@import "@/styles/variables.scss";

// ─── Component-local design tokens ────────────────────────────────────────
$trigger-focus-width: 2px;
$trigger-focus-offset: 4px;

$dialog-overlay-bg: rgba($book-cover-bg, 0.42);
$dialog-overlay-blur: 28px;
$dialog-overlay-saturate: 140%;

$viewer-padding-y: 80px;
$viewer-padding-x: 24px;
$viewer-padding-y-mobile: 64px;
$viewer-padding-x-mobile: 16px;

$viewer-image-max-h: min(86vh, 1100px);
$viewer-image-max-w: min(86vw, 800px);
$viewer-image-max-h-mobile: 80vh;
$viewer-image-max-w-mobile: 92vw;
$viewer-image-shadow:
  drop-shadow(0 1px 2px rgba(black, 0.18))
  drop-shadow(0 12px 28px rgba(black, 0.28))
  drop-shadow(0 30px 60px rgba(black, 0.18));

$close-button-size: 44px;
$close-button-offset: 20px;
$close-button-radius: map-get($rounded, 'pill');
$close-button-bg: rgba(white, 0.94);
$close-button-bg-hover: white;
$close-button-border: 1px solid rgba($book-ink, 0.06);
$close-button-shadow:
  0 1px 0 rgba(white, 1) inset,
  0 8px 20px rgba(black, 0.12),
  0 2px 6px rgba(black, 0.06);
$close-button-hover-scale: 1.04;

$lens-bg: rgba(white, 0.18);
$lens-border: 1px solid rgba(white, 0.85);
$lens-shadow:
  0 0 0 1px rgba(black, 0.35),
  0 4px 12px rgba(black, 0.25);
$lens-saturate: 140%;

$panel-gutter: 16px;
$panel-bg: $book-grey-lightest;
$panel-border: 1px solid $book-grey-lighter;
$panel-radius: $border-radius-root;
$panel-shadow:
  0 12px 32px rgba(black, 0.18),
  0 2px 8px rgba(black, 0.08);

// ─── Trigger (the small cover on the product page) ────────────────────────
.cover-enlargeable {
  position: relative;
}

.cover-enlargeable__trigger {
  display: block;
  position: relative;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;

  &:focus-visible {
    outline: $trigger-focus-width solid $book-red;
    outline-offset: $trigger-focus-offset;
  }
}

// ─── Hover-zoom (Amazon-style lens + side panel) ──────────────────────────
.cover-enlargeable__lens {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  background: $lens-bg;
  border: $lens-border;
  box-shadow: $lens-shadow;
  backdrop-filter: saturate($lens-saturate);
  -webkit-backdrop-filter: saturate($lens-saturate);
  will-change: transform;
}

.cover-enlargeable__zoom-panel {
  position: absolute;
  top: 0;
  left: calc(100% + #{$panel-gutter});
  pointer-events: none;
  background-color: $panel-bg;
  background-repeat: no-repeat;
  border-radius: $panel-radius;
  border: $panel-border;
  box-shadow: $panel-shadow;
  z-index: $z-dropdown;
}

@media (hover: none), (pointer: coarse) {
  .cover-enlargeable__lens,
  .cover-enlargeable__zoom-panel {
    display: none !important;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .cover-enlargeable__lens,
  .cover-enlargeable__zoom-panel {
    display: none !important;
  }
}

// ─── Click-to-enlarge dialog ──────────────────────────────────────────────
// Vuetify dialog layers must be transparent from frame 1 — otherwise
// backdrop-filter samples the white v-dialog surface instead of the page,
// producing a brief opaque flash before settling into the blur.
::v-deep .cover-enlargeable__dialog,
::v-deep .v-dialog.cover-enlargeable__dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden;
}

.cover-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $viewer-padding-y $viewer-padding-x;
  background: $dialog-overlay-bg;
  backdrop-filter: blur($dialog-overlay-blur) saturate($dialog-overlay-saturate);
  -webkit-backdrop-filter: blur($dialog-overlay-blur) saturate($dialog-overlay-saturate);
  user-select: none;
}

.cover-viewer__image {
  max-height: $viewer-image-max-h;
  max-width: $viewer-image-max-w;
  filter: $viewer-image-shadow;
}

.cover-viewer__close.v-btn--icon {
  position: absolute;
  top: max(#{$close-button-offset}, env(safe-area-inset-top, #{$close-button-offset}));
  right: max(#{$close-button-offset}, env(safe-area-inset-right, #{$close-button-offset}));
  width: $close-button-size !important;
  height: $close-button-size !important;
  color: $book-ink !important;
  background: $close-button-bg !important;
  border: $close-button-border;
  border-radius: $close-button-radius !important;
  box-shadow: $close-button-shadow;

  &:hover {
    background: $close-button-bg-hover !important;
    transform: scale($close-button-hover-scale);
  }

  &::before {
    background: transparent !important;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .cover-viewer {
    padding: $viewer-padding-y-mobile $viewer-padding-x-mobile;
  }

  .cover-viewer__image {
    max-height: $viewer-image-max-h-mobile;
    max-width: $viewer-image-max-w-mobile;
  }
}
</style>
